import React, { useEffect, useState, useRef, useCallback } from 'react';

import styled from 'styled-components';

const POSITIONS = {
    TOP: 'top',
    BOTTOM: 'bottom',
    LEFT: 'left',
    RIGHT: 'right',
} as const;

type Position = typeof POSITIONS[keyof typeof POSITIONS];

interface Coordinates {
    top: number;
    left: number;
}

interface StyledArrowProps {
    position: Position;
    size: number;
}

interface AampePopoverProps {
    isOpen: boolean;
    onClose: () => void;
    triggerRef: React.RefObject<HTMLElement>;
    children: React.ReactNode;
    preferredPosition?: Position;
    offset?: number;
    showArrow?: boolean;
    className?: string;
}

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 40;
    background-color: rgba(0, 0, 0, 0.1);
`;

const PopoverContainer = styled.div<{ visible: boolean }>`
    position: fixed;
    z-index: 50;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -1px rgba(0, 0, 0, 0.06);
    padding: 1rem;
    min-width: 200px;
    opacity: ${(props) => (props.visible ? 1 : 0)};
    visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
    transition: opacity 0.1s ease-out;
`;

const Arrow = styled.div<StyledArrowProps>`
    position: absolute;
    width: 0;
    height: 0;

    ${({ position, size }) => {
        const borderSize = `${size}px`;
        const baseBorder = `${borderSize} solid transparent`;

        // For left/right positions, we swap which borders create the arrow
        if (position === POSITIONS.LEFT || position === POSITIONS.RIGHT) {
            return `
        border-top: ${baseBorder};
        border-bottom: ${baseBorder};
        border-right: ${borderSize} solid white;
        transform: ${position === POSITIONS.LEFT ? 'rotate(180deg)' : 'none'};
        `;
        }

        return `
        border-left: ${baseBorder};
        border-right: ${baseBorder};
        border-bottom: ${borderSize} solid white;
        transform: ${position === POSITIONS.TOP ? 'rotate(180deg)' : 'none'};
    `;
    }}
`;

const AampePopover: React.FC<AampePopoverProps> = ({
    isOpen,
    onClose,
    triggerRef,
    children,
    preferredPosition = POSITIONS.BOTTOM,
    offset = 8,
    showArrow = true,
    className = '',
}) => {
    const [position, setPosition] = useState<Coordinates>({ top: 0, left: 0 });
    const [arrowPosition, setArrowPosition] = useState<Coordinates>({
        top: 0,
        left: 0,
    });
    const [actualPosition, setActualPosition] =
        useState<Position>(preferredPosition);
    const [isPositioned, setIsPositioned] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const updatePositionTimeoutRef = useRef<number | null>(null);

    const ARROW_SIZE = 8;

    const calculatePosition = useCallback((): {
        newTop: number;
        newLeft: number;
        arrowTop: number;
        arrowLeft: number;
        newPosition: Position;
    } | null => {
        if (!triggerRef.current || !popoverRef.current) return null;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const popoverRect = popoverRef.current.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        let newTop = 0;
        let newLeft = 0;
        let arrowTop = 0;
        let arrowLeft = 0;
        let newPosition = preferredPosition;

        const spaceAbove = triggerRect.top;
        const spaceBelow = windowHeight - triggerRect.bottom;
        const spaceLeft = triggerRect.left;
        const spaceRight = windowWidth - triggerRect.right;

        const centerHorizontally = (): number =>
            triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;

        const centerVertically = (): number =>
            triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2;

        const isOffscreen = (top: number, left: number): boolean => {
            return (
                top < offset ||
                left < offset ||
                top + popoverRect.height > windowHeight - offset ||
                left + popoverRect.width > windowWidth - offset
            );
        };

        switch (preferredPosition) {
            case POSITIONS.BOTTOM:
                newTop = triggerRect.bottom + offset;
                newLeft = centerHorizontally();
                arrowTop = -ARROW_SIZE;
                arrowLeft = popoverRect.width / 2 - ARROW_SIZE;

                if (
                    isOffscreen(newTop, newLeft) &&
                    spaceAbove >= popoverRect.height + offset
                ) {
                    newPosition = POSITIONS.TOP;
                    newTop = triggerRect.top - popoverRect.height - offset;
                    arrowTop = popoverRect.height;
                    arrowLeft = popoverRect.width / 2 - ARROW_SIZE;
                }
                break;

            case POSITIONS.TOP:
                newTop = triggerRect.top - popoverRect.height - offset;
                newLeft = centerHorizontally();
                arrowTop = popoverRect.height;
                arrowLeft = popoverRect.width / 2 - ARROW_SIZE;

                if (
                    isOffscreen(newTop, newLeft) &&
                    spaceBelow >= popoverRect.height + offset
                ) {
                    newPosition = POSITIONS.BOTTOM;
                    newTop = triggerRect.bottom + offset;
                    arrowTop = -ARROW_SIZE;
                    arrowLeft = popoverRect.width / 2 - ARROW_SIZE;
                }
                break;

            case POSITIONS.LEFT:
                newTop = centerVertically();
                newLeft = triggerRect.left - popoverRect.width - offset;
                arrowTop = popoverRect.height / 2 - ARROW_SIZE;
                arrowLeft = popoverRect.width; // Position arrow at the right edge

                if (
                    isOffscreen(newTop, newLeft) &&
                    spaceRight >= popoverRect.width + offset
                ) {
                    newPosition = POSITIONS.RIGHT;
                    newLeft = triggerRect.right + offset;
                    arrowLeft = -ARROW_SIZE; // Position arrow at the left edge
                }
                break;

            case POSITIONS.RIGHT:
                newTop = centerVertically();
                newLeft = triggerRect.right + offset;
                arrowTop = popoverRect.height / 2 - ARROW_SIZE;
                arrowLeft = -ARROW_SIZE; // Position arrow at the left edge

                if (
                    isOffscreen(newTop, newLeft) &&
                    spaceLeft >= popoverRect.width + offset
                ) {
                    newPosition = POSITIONS.LEFT;
                    newLeft = triggerRect.left - popoverRect.width - offset;
                    arrowLeft = popoverRect.width; // Position arrow at the right edge
                }
                break;
        }

        // Ensure popover stays within viewport bounds
        newLeft = Math.max(
            offset,
            Math.min(newLeft, windowWidth - popoverRect.width - offset),
        );
        newTop = Math.max(
            offset,
            Math.min(newTop, windowHeight - popoverRect.height - offset),
        );

        // Adjust arrow position if popover position was constrained
        if (newPosition === POSITIONS.TOP || newPosition === POSITIONS.BOTTOM) {
            arrowLeft = Math.max(
                ARROW_SIZE,
                Math.min(arrowLeft, popoverRect.width - ARROW_SIZE * 2),
            );
        } else {
            arrowTop = Math.max(
                ARROW_SIZE,
                Math.min(arrowTop, popoverRect.height - ARROW_SIZE * 2),
            );
        }

        return { newTop, newLeft, arrowTop, arrowLeft, newPosition };
    }, [preferredPosition, offset, ARROW_SIZE, triggerRef, popoverRef]);

    const updatePosition = useCallback((): void => {
        const positions = calculatePosition();
        if (!positions) return;

        const { newTop, newLeft, arrowTop, arrowLeft, newPosition } = positions;

        setPosition({ top: newTop, left: newLeft });
        setArrowPosition({ top: arrowTop, left: arrowLeft });
        setActualPosition(newPosition);
        setIsPositioned(true);
    }, [calculatePosition]);

    const debouncedUpdatePosition = useCallback(() => {
        if (updatePositionTimeoutRef.current) {
            window.cancelAnimationFrame(updatePositionTimeoutRef.current);
        }
        updatePositionTimeoutRef.current =
            window.requestAnimationFrame(updatePosition);
    }, [updatePosition]);

    // Initial positioning effect
    useEffect(() => {
        if (isOpen) {
            setIsPositioned(false);
            const timer = setTimeout(() => {
                updatePosition();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [isOpen, updatePosition]);

    // Observer and event listener effect
    useEffect(() => {
        if (!isOpen) return;

        if (popoverRef.current) {
            resizeObserverRef.current = new ResizeObserver(
                debouncedUpdatePosition,
            );
            resizeObserverRef.current.observe(popoverRef.current);
        }

        const mutationObserver = new MutationObserver(debouncedUpdatePosition);

        if (popoverRef.current) {
            mutationObserver.observe(popoverRef.current, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true,
            });
        }

        const handleScroll = (event: Event) => {
            const target = event.target as Node;
            if (popoverRef.current?.contains(target)) return;
            debouncedUpdatePosition();
        };

        window.addEventListener('resize', debouncedUpdatePosition);
        window.addEventListener('scroll', handleScroll, true);
        document.addEventListener('animationend', debouncedUpdatePosition);

        return () => {
            if (updatePositionTimeoutRef.current) {
                window.cancelAnimationFrame(updatePositionTimeoutRef.current);
            }
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
            mutationObserver.disconnect();
            window.removeEventListener('resize', debouncedUpdatePosition);
            window.removeEventListener('scroll', handleScroll, true);
            document.removeEventListener(
                'animationend',
                debouncedUpdatePosition,
            );
        };
    }, [isOpen, debouncedUpdatePosition]);

    // Update position when children change
    useEffect(() => {
        if (isOpen) {
            debouncedUpdatePosition();
        }
    }, [isOpen, children, debouncedUpdatePosition]);

    if (!isOpen) return null;

    return (
        <>
            <Backdrop onClick={onClose} />
            <PopoverContainer
                ref={popoverRef}
                style={{
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                }}
                className={className}
                visible={isPositioned}
            >
                {showArrow && (
                    <Arrow
                        style={{
                            top: `${arrowPosition.top}px`,
                            left: `${arrowPosition.left}px`,
                        }}
                        position={actualPosition}
                        size={ARROW_SIZE}
                    />
                )}
                {children}
            </PopoverContainer>
        </>
    );
};

export default AampePopover;
